import { apiClient } from './client';
import { CursorPage, GlobalRole, ModerationLog, ServerStats, User } from '@/types';

export const usersApi = {
  async search(q: string, limit?: number) {
    const res = await apiClient.get<User[]>('/api/users/search', { params: { q, limit } });
    return res.data;
  },

  async getProfile(userId: string) {
    const res = await apiClient.get<User>(`/api/users/${userId}`);
    return res.data;
  },

  async updateProfile(data: { displayName?: string; bio?: string }) {
    const res = await apiClient.put<User>('/api/users/profile', data);
    return res.data;
  },

  async uploadAvatar(file: File) {
    const form = new FormData();
    form.append('avatar', file);
    const res = await apiClient.post<{ avatarUrl: string }>('/api/users/avatar', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },
};

export const adminApi = {
  async getStats(): Promise<ServerStats> {
    const res = await apiClient.get<ServerStats>('/api/admin/stats');
    return res.data;
  },

  async listUsers(params?: { cursor?: string; limit?: number; search?: string }) {
    const res = await apiClient.get<CursorPage<User & { email: string }>>('/api/admin/users', { params });
    return res.data;
  },

  async setUserRole(userId: string, role: GlobalRole) {
    const res = await apiClient.put<User>(`/api/admin/users/${userId}/role`, { role });
    return res.data;
  },

  async banUser(userId: string, reason?: string) {
    await apiClient.post(`/api/admin/users/${userId}/ban`, { reason });
  },

  async unbanUser(userId: string) {
    await apiClient.post(`/api/admin/users/${userId}/unban`);
  },

  async deleteUser(userId: string) {
    await apiClient.delete(`/api/admin/users/${userId}`);
  },

  // Phase 13 — manual email verify (для случаев когда SMTP не дошло
  // и юзер залочен: forgotPassword silently no-op для !emailVerified).
  async verifyUser(userId: string) {
    const res = await apiClient.post<User>(`/api/admin/users/${userId}/verify`);
    return res.data;
  },

  async listChannels(params?: { cursor?: string; limit?: number; search?: string }) {
    const res = await apiClient.get('/api/admin/channels', { params });
    return res.data;
  },

  async deleteChannel(channelId: string) {
    await apiClient.delete(`/api/admin/channels/${channelId}`);
  },

  async setChannelDefault(channelId: string, isDefault: boolean) {
    const res = await apiClient.put(`/api/admin/channels/${channelId}/default`, { isDefault });
    return res.data;
  },

  async updateChannelAsAdmin(channelId: string, data: {
    displayName?: string;
    topic?: string | null;
    description?: string | null;
    mediaEnabled?: boolean;
    reactionsEnabled?: boolean;
    membersCanPost?: boolean;
    chatStyle?: 'BUBBLE' | 'CLASSIC';
  }) {
    const res = await apiClient.put(`/api/channels/${channelId}`, data);
    return res.data;
  },

  /** Fetch karma top list — returns Map<userId, score>. SUPER_ADMIN only. */
  async getKarmaMap(limit = 500): Promise<Map<string, number>> {
    try {
      const res = await apiClient.get<Array<{ userId: string; score: number }>>('/api/admin/karma/top', { params: { limit } });
      return new Map(res.data.map((r) => [r.userId, r.score]));
    } catch {
      return new Map();
    }
  },

  async getModerationLog(params?: { cursor?: string; limit?: number; channelId?: string }) {
    const res = await apiClient.get<CursorPage<ModerationLog>>('/api/admin/modlog', { params });
    return res.data;
  },
};
